import React, { useEffect, useState, useCallback } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Repeat, Play, Pause, Trash2, Plus, Zap } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";

const FREQ_LABEL = {
  monthly: "Monatlich",
  quarterly: "Quartalsweise",
  yearly: "Jährlich",
};

const VAT_RATES = [
  { label: "0%", value: 0 },
  { label: "2.5%", value: 0.025 },
  { label: "7.7%", value: 0.077 },
];

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800",
  paused: "bg-amber-100 text-amber-800",
  completed: "bg-slate-200 text-slate-700",
};

export function RecurringInvoices() {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    title: "",
    vat_rate: 0.077,
    frequency: "monthly",
    day_of_month: 1,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    due_days: 30,
    notes: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
  });

  const companyId = localStorage.getItem("demoCompanyId");

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [tplRes, custRes] = await Promise.all([
        apiClient.get(`/companies/${companyId}/recurring-invoices`),
        apiClient.get(`/companies/${companyId}/contacts`),
      ]);
      setTemplates(tplRes.data || []);
      setCustomers((custRes.data || []).filter((c) => c.type === "customer"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const addLine = () =>
    setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] }));

  const removeLine = (idx) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const updateLine = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, [key]: value } : li)),
    }));

  const subtotal = form.line_items.reduce(
    (acc, li) => acc + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0),
    0,
  );
  const taxAmount = subtotal * form.vat_rate;
  const total = subtotal + taxAmount;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.title) {
      toast.error("Bitte Kunde und Titel angeben.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        title: form.title,
        currency: "CHF",
        line_items: form.line_items.map((li) => ({
          description: li.description,
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
        })),
        subtotal: parseFloat(subtotal.toFixed(2)),
        tax_amount: parseFloat(taxAmount.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        frequency: form.frequency,
        day_of_month: parseInt(form.day_of_month, 10) || 1,
        start_date: form.start_date,
        end_date: form.end_date || null,
        due_days: parseInt(form.due_days, 10) || 30,
        notes: form.notes || null,
      };
      await apiClient.post(`/companies/${companyId}/recurring-invoices`, payload);
      toast.success("Wiederkehrende Rechnung erstellt.");
      setOpenDialog(false);
      setForm({
        customer_id: "",
        title: "",
        vat_rate: 0.077,
        frequency: "monthly",
        day_of_month: 1,
        start_date: new Date().toISOString().slice(0, 10),
        end_date: "",
        due_days: 30,
        notes: "",
        line_items: [{ description: "", quantity: 1, unit_price: 0 }],
      });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handlePauseResume = async (tpl) => {
    const newStatus = tpl.status === "active" ? "paused" : "active";
    try {
      await apiClient.put(`/recurring-invoices/${tpl._id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Vorlage reaktiviert." : "Vorlage pausiert.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Statuswechsel fehlgeschlagen.");
    }
  };

  const handleGenerateNow = async (tpl) => {
    try {
      const res = await apiClient.post(`/recurring-invoices/${tpl._id}/generate-now`);
      toast.success(`Rechnung erstellt (${res.data.invoice_ids?.length || 0}).`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generierung fehlgeschlagen.");
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Vorlage "${tpl.title}" wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/recurring-invoices/${tpl._id}`);
      toast.success("Vorlage gelöscht.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const handleRunScheduler = async () => {
    try {
      const res = await apiClient.post("/scheduler/recurring-invoices/run-now");
      const n = res.data.generated_count || 0;
      if (n === 0) {
        toast.info("Keine fälligen Vorlagen — alles aktuell.");
      } else {
        toast.success(`${n} Rechnung(en) automatisch erstellt.`);
      }
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scheduler-Lauf fehlgeschlagen.");
    }
  };

  return (
    <div className="space-y-4" data-testid="recurring-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Repeat className="h-6 w-6 text-violet-600" />
            Wiederkehrende Rechnungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Abos / Mietzins / Hosting-Gebühren — werden täglich um 02:00 Uhr automatisch generiert.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={handleRunScheduler}
            data-testid="recurring-run-scheduler-btn"
          >
            <Zap className="h-4 w-4 mr-1.5" />
            Jetzt prüfen
          </Button>
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="recurring-new-btn">
                <Plus className="h-4 w-4 mr-1.5" />
                Neue Vorlage
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recurring-form-dialog">
              <DialogHeader>
                <DialogTitle>Neue wiederkehrende Rechnung</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Kunde</Label>
                    <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                      <SelectTrigger data-testid="recurring-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Titel / Bezeichnung</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="z.B. Hosting-Gebühr Q1"
                      data-testid="recurring-title-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Frequenz</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monatlich</SelectItem>
                        <SelectItem value="quarterly">Quartalsweise</SelectItem>
                        <SelectItem value="yearly">Jährlich</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Stichtag (1-28)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={form.day_of_month}
                      onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                      data-testid="recurring-day-input"
                    />
                  </div>
                  <div>
                    <Label>Zahlungsfrist (Tage)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="180"
                      value={form.due_days}
                      onChange={(e) => setForm({ ...form, due_days: e.target.value })}
                      data-testid="recurring-due-days-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Start-Datum</Label>
                    <SwissDateField
                      
                      value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      data-testid="recurring-start-date-input"
                    />
                  </div>
                  <div>
                    <Label>End-Datum (optional)</Label>
                    <SwissDateField
                      
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      data-testid="recurring-end-date-input"
                    />
                  </div>
                  <div>
                    <Label>MWST</Label>
                    <Select
                      value={String(form.vat_rate)}
                      onValueChange={(v) => setForm({ ...form, vat_rate: parseFloat(v) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VAT_RATES.map((vr) => (
                          <SelectItem key={vr.value} value={String(vr.value)}>{vr.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Positionen</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="recurring-add-line-btn">
                      + Position
                    </Button>
                  </div>
                  {form.line_items.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 mb-2">
                      <Input
                        className="col-span-6"
                        placeholder="Beschreibung"
                        value={li.description}
                        onChange={(e) => updateLine(idx, "description", e.target.value)}
                        data-testid={`recurring-line-desc-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        placeholder="Menge"
                        value={li.quantity}
                        onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                      />
                      <Input
                        className="col-span-3"
                        type="number"
                        placeholder="Preis"
                        value={li.unit_price}
                        onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="col-span-1"
                        onClick={() => removeLine(idx)}
                        disabled={form.line_items.length === 1}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="bg-slate-50 rounded-md p-3 text-sm">
                  <div className="flex justify-between"><span>Zwischensumme:</span><span>CHF {subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>MWST ({(form.vat_rate * 100).toFixed(1)}%):</span><span>CHF {taxAmount.toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold border-t mt-1 pt-1"><span>Total:</span><span>CHF {total.toFixed(2)}</span></div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Abbrechen</Button>
                  <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="recurring-save-btn">
                    {saving ? "Speichern…" : "Vorlage anlegen"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border border-slate-200 bg-white shadow-sm">
        {loading && <p className="p-4 text-sm text-slate-400">Lade…</p>}
        {!loading && templates.length === 0 && (
          <EmptyState
            testid="recurring-empty"
            icon={<Repeat className="h-5 w-5" />}
            title="Noch keine wiederkehrenden Rechnungen"
            description="Lege eine Vorlage für monatliche Mieten, Hosting-Gebühren oder Service-Verträge an. Das System erstellt dann automatisch jeden Monat eine neue Rechnung."
          />
        )}
        {!loading && templates.length > 0 && (
          <Table data-testid="recurring-table">
            <TableHeader>
              <TableRow>
                <TableHead>Titel</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>Frequenz</TableHead>
                <TableHead>Nächste Ausführung</TableHead>
                <TableHead>Total (CHF)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bisher erstellt</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl._id} data-testid={`recurring-row-${tpl._id}`}>
                  <TableCell className="font-medium">{tpl.title}</TableCell>
                  <TableCell>{tpl.customer_name || tpl.customer_id}</TableCell>
                  <TableCell>{FREQ_LABEL[tpl.frequency] || tpl.frequency}</TableCell>
                  <TableCell>{formatDate(tpl.next_run_date)}</TableCell>
                  <TableCell>{tpl.total?.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLORS[tpl.status]}>{tpl.status}</Badge>
                  </TableCell>
                  <TableCell>{tpl.generated_count}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {tpl.status !== "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePauseResume(tpl)}
                        data-testid={`recurring-toggle-${tpl._id}`}
                      >
                        {tpl.status === "active" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {tpl.status === "active" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGenerateNow(tpl)}
                        data-testid={`recurring-generate-${tpl._id}`}
                      >
                        Jetzt erstellen
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-rose-600"
                      onClick={() => handleDelete(tpl)}
                      data-testid={`recurring-delete-${tpl._id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default RecurringInvoices;
